import { Moment } from 'moment'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ButtonCustom,
  ButtonWrapper,
  DatePickerCustom,
  Icon,
  RangeDateWrapper,
  SearchDayWrapper,
  TitleSearch,
  WrapperFilter,
} from '@/components/pages/ClientDevice/styles'
import { dateTime } from '@/configs'
import { defaultFilterDate, FilterDate } from '@/pages/ClientDevice'
import { DateType } from '@/types'
import {
  ExportClientDeviceByDayRequest,
  ExportClientDeviceByRangeDayRequest,
  ExportTotalClientDeviceByRangeDayRequest,
  GetClientDeviceByDayRequest,
  GetClientDeviceByRangeDayRequest,
  GetTotalClientDeviceByRangeDayRequest,
} from '@/types/apis/clientDevice'
import { formatDateSendRequest } from '@/utils/date'

type Props = {
  setDateFilter: React.Dispatch<React.SetStateAction<Moment | null | undefined>>
  getClientDeviceByDay: (request: GetClientDeviceByDayRequest) => Promise<void>
  exportExcelClientDeviceByDay: (request: ExportClientDeviceByDayRequest) => Promise<void>
  dateFilter?: Moment | null
  getClientDeviceByRangeDay: (request: GetClientDeviceByRangeDayRequest) => Promise<void>
  exportExcelClientDeviceByRangeDay: (request: ExportClientDeviceByRangeDayRequest) => Promise<void>
  getTotalClientDeviceByRangeDay: (request: GetTotalClientDeviceByRangeDayRequest) => Promise<void>
  exportExcelTotalClientDeviceByRangeDay: (request: ExportTotalClientDeviceByRangeDayRequest) => Promise<void>
  setDateRangeFilter: React.Dispatch<React.SetStateAction<FilterDate>>
  dateRangeFilter: FilterDate
  exportClientDeviceLoading: boolean
  exportClientDeviceByDayLoading: boolean
  exportClientDeviceByRangeDayLoading: boolean
}

const FilterClientDeviceByDay: FC<Props> = ({
  getClientDeviceByDay,
  exportExcelClientDeviceByDay,
  getClientDeviceByRangeDay,
  exportExcelClientDeviceByRangeDay,
  getTotalClientDeviceByRangeDay,
  exportExcelTotalClientDeviceByRangeDay,
  setDateFilter,
  dateFilter,
  dateRangeFilter,
  setDateRangeFilter,
  exportClientDeviceByDayLoading,
  exportClientDeviceByRangeDayLoading,
  exportClientDeviceLoading,
}) => {
  const { t } = useTranslation()

  const handlePickByDay = (date: Moment | null) => {
    setDateRangeFilter((pre) => ({ ...pre, type: DateType.BY_DAY }))
    setDateFilter(date)
    getClientDeviceByDay({ date: formatDateSendRequest(date) })
  }

  const handlePickRangeDay = (type: string, date: Moment | null) => {
    switch (type) {
      case 'fromByTotalDate':
        setDateRangeFilter((pre) => ({ ...pre, type: DateType.TOTAL_RANGE_DAY, totalFromDate: date }))
        date &&
          dateRangeFilter.totalToDate &&
          getTotalClientDeviceByRangeDay({
            fromDate: formatDateSendRequest(date),
            toDate: formatDateSendRequest(dateRangeFilter.totalToDate),
          })
        break

      case 'toByTotalDate':
        setDateRangeFilter((pre) => ({
          ...pre,
          type: DateType.TOTAL_RANGE_DAY,
          totalToDate: date,
        }))
        getTotalClientDeviceByRangeDay({
          fromDate: formatDateSendRequest(dateRangeFilter.totalFromDate),
          toDate: formatDateSendRequest(date),
        })
        break

      case 'fromByRangeDate':
        setDateRangeFilter((pre) => ({ ...pre, type: DateType.RANGE_DAY, fromDate: date }))
        date &&
          dateRangeFilter.toDate &&
          getClientDeviceByRangeDay({
            fromDate: formatDateSendRequest(date),
            toDate: formatDateSendRequest(dateRangeFilter.toDate),
          })
        break

      case 'toByRangeDate':
        setDateRangeFilter((pre) => ({ ...pre, type: DateType.RANGE_DAY, toDate: date }))
        getClientDeviceByRangeDay({
          fromDate: formatDateSendRequest(dateRangeFilter.fromDate),
          toDate: formatDateSendRequest(date),
        })
        break

      default:
        break
    }
  }

  const handleExportCSV = () => {
    if (dateFilter) {
      exportExcelClientDeviceByDay({ date: formatDateSendRequest(dateFilter) })
    }
  }

  const handleExportCSVClientByRangeDay = () => {
    if (dateRangeFilter.fromDate && dateRangeFilter.toDate) {
      exportExcelClientDeviceByRangeDay({
        fromDate: formatDateSendRequest(dateRangeFilter.fromDate),
        toDate: formatDateSendRequest(dateRangeFilter.toDate),
      })
    }
  }

  const handleExportCSVTotalByRangeDay = () => {
    if (dateRangeFilter.totalFromDate && dateRangeFilter.totalToDate) {
      exportExcelTotalClientDeviceByRangeDay({
        fromDate: formatDateSendRequest(dateRangeFilter.totalFromDate),
        toDate: formatDateSendRequest(dateRangeFilter.totalToDate),
      })
    }
  }

  const handleDownLoadCSV = (type: DateType) => {
    switch (type) {
      case 1:
        handleExportCSV()
        break
      case 2:
        handleExportCSVClientByRangeDay()
        break
      case 3:
        handleExportCSVTotalByRangeDay()
        break
      default:
        break
    }
  }

  const handleReset = () => {
    setDateFilter(null)
    setDateRangeFilter(defaultFilterDate)
  }

  return (
    <WrapperFilter>
      <SearchDayWrapper width="31%">
        <TitleSearch>{t('components.pages.clientDevice.listReport.filter.filterDay')}</TitleSearch>
        <DatePickerCustom
          value={dateFilter}
          disabled={dateRangeFilter.type !== DateType.BY_DAY && dateRangeFilter.type !== DateType.NO_SELECT}
          format={dateTime.dateFormat}
          onChange={(date) => handlePickByDay(date)}
        />
      </SearchDayWrapper>
      <ButtonWrapper>
        <SearchDayWrapper>
          <TitleSearch>{t('components.pages.clientDevice.listReport.filter.filterByRangeDay')}</TitleSearch>
          <RangeDateWrapper>
            <DatePickerCustom
              value={dateRangeFilter.fromDate}
              onChange={(date) => handlePickRangeDay('fromByRangeDate', date)}
              format={dateTime.dateFormat}
              disabled={dateRangeFilter.type !== DateType.RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT}
            />
            <Icon>~</Icon>
            <DatePickerCustom
              value={dateRangeFilter.toDate}
              onChange={(date) => handlePickRangeDay('toByRangeDate', date)}
              format={dateTime.dateFormat}
              disabled={dateRangeFilter.type !== DateType.RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT}
            />
          </RangeDateWrapper>
        </SearchDayWrapper>
        {dateRangeFilter.type !== DateType.NO_SELECT && (
          <ButtonCustom danger onClick={handleReset}>
            {t('components.pages.clientDevice.listReport.table.reset')}
          </ButtonCustom>
        )}
      </ButtonWrapper>
      <ButtonWrapper>
        <SearchDayWrapper>
          <TitleSearch>{t('components.pages.clientDevice.listReport.filter.filterByTime')}</TitleSearch>
          <RangeDateWrapper>
            <DatePickerCustom
              value={dateRangeFilter.totalFromDate}
              onChange={(date) => handlePickRangeDay('fromByTotalDate', date)}
              format={dateTime.dateFormat}
              disabled={
                dateRangeFilter.type !== DateType.TOTAL_RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT
              }
            />
            <Icon>~</Icon>
            <DatePickerCustom
              value={dateRangeFilter.totalToDate}
              onChange={(date) => handlePickRangeDay('toByTotalDate', date)}
              format={dateTime.dateFormat}
              disabled={
                dateRangeFilter.type !== DateType.TOTAL_RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT
              }
            />
          </RangeDateWrapper>
        </SearchDayWrapper>
        <ButtonCustom
          marginLeft="30px"
          loading={exportClientDeviceByDayLoading || exportClientDeviceByRangeDayLoading || exportClientDeviceLoading}
          onClick={() => handleDownLoadCSV(dateRangeFilter.type)}
        >
          {t('components.pages.clientDevice.listReport.table.export')}
        </ButtonCustom>
      </ButtonWrapper>
    </WrapperFilter>
  )
}

FilterClientDeviceByDay.displayName = 'FilterClientDeviceByDay'
export default FilterClientDeviceByDay
